/**
 * useQuoteWizardAutoSave - localStorage auto-save for the quote wizard
 *
 * Persists wizard state to localStorage on each step change and on a 3-second
 * debounce, so in-progress quotes survive tab crashes and accidental navigation.
 *
 * Key format: `quote-wizard-draft-{orgId}` (new) or `quote-wizard-draft-{quoteId}` (edit)
 * Expiry: 7 days
 *
 * @example
 * ```tsx
 * const { hasSavedDraft, resumeDraft, discardDraft } = useQuoteWizardAutoSave(
 *   wizardState,
 *   orgId
 * );
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { QuoteWizardData } from '../../types/quote';

// ============================================================================
// Types
// ============================================================================

interface SavedWizardState {
  wizardData: QuoteWizardData;
  currentStep: number;
  savedAt: number; // epoch ms
}

export interface UseQuoteWizardAutoSaveReturn {
  /** Whether a saved draft was found in localStorage on mount */
  hasSavedDraft: boolean;
  /** The timestamp of the saved draft (null if none) */
  savedDraftAt: Date | null;
  /** Restore the saved wizard state (call from recovery banner) */
  resumeDraft: () => void;
  /** Discard the saved state without restoring it */
  discardDraft: () => void;
  /** Clear the saved state on successful submit/save */
  clearDraft: () => void;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Every quote-wizard draft key starts with this. Exported so the shared-device wipe
 * (clearLocalFormDrafts, run when a DIFFERENT user signs in) clears quote drafts too (#3924):
 * the keys are org- or quote-scoped, not user-scoped, so they would otherwise outlive the rep.
 */
export const QUOTE_WIZARD_DRAFT_PREFIX = 'quote-wizard-draft-';
const STORAGE_PREFIX = QUOTE_WIZARD_DRAFT_PREFIX;
const DEBOUNCE_MS = 3000;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ============================================================================
// Helpers
// ============================================================================

function buildKey(id: string): string {
  return `${STORAGE_PREFIX}${id}`;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Auto-save hook for the quote wizard.
 *
 * @param wizardData - Current wizard form data
 * @param currentStep - Current step index
 * @param draftKey - Unique key for this draft: orgId for new quotes, quoteId for edits
 * @param onResume - Callback invoked with the restored state when the user clicks "Resume"
 */
export function useQuoteWizardAutoSave(
  wizardData: QuoteWizardData,
  currentStep: number,
  draftKey: string | undefined,
  onResume: (state: { wizardData: QuoteWizardData; currentStep: number }) => void
): UseQuoteWizardAutoSaveReturn {
  const key = draftKey ? buildKey(draftKey) : null;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved state on mount — only checked once
  const [savedDraft, setSavedDraft] = useState<{
    wizardData: QuoteWizardData;
    currentStep: number;
    savedAt: Date;
  } | null>(() => {
    if (!key) return null;

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const parsed: SavedWizardState = JSON.parse(raw);

      // Expire old saves
      if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
        localStorage.removeItem(key);
        return null;
      }

      return {
        wizardData: parsed.wizardData,
        currentStep: parsed.currentStep,
        savedAt: new Date(parsed.savedAt),
      };
    } catch {
      return null;
    }
  });

  // Write to localStorage (debounced)
  const persist = useCallback(
    (data: QuoteWizardData, step: number) => {
      if (!key) return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        try {
          const state: SavedWizardState = {
            wizardData: data,
            currentStep: step,
            savedAt: Date.now(),
          };
          localStorage.setItem(key, JSON.stringify(state));
        } catch (err) {
          // localStorage full or blocked — fail silently
          console.warn('Quote wizard auto-save failed:', err);
        }
      }, DEBOUNCE_MS);
    },
    [key]
  );

  // Persist whenever wizardData or currentStep changes
  useEffect(() => {
    persist(wizardData, currentStep);
  }, [wizardData, currentStep, persist]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const resumeDraft = useCallback(() => {
    if (!savedDraft) return;
    onResume({ wizardData: savedDraft.wizardData, currentStep: savedDraft.currentStep });
    setSavedDraft(null);
    if (key) localStorage.removeItem(key);
  }, [savedDraft, onResume, key]);

  const discardDraft = useCallback(() => {
    setSavedDraft(null);
    if (key) localStorage.removeItem(key);
  }, [key]);

  const clearDraft = useCallback(() => {
    setSavedDraft(null);
    if (key) localStorage.removeItem(key);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, [key]);

  return {
    hasSavedDraft: savedDraft !== null,
    savedDraftAt: savedDraft?.savedAt ?? null,
    resumeDraft,
    discardDraft,
    clearDraft,
  };
}
